import type { DataBody } from './protocol'

/**
 * Where the replica stands in the host's stream of data messages. Every
 * snapshot, patch and relayed action carries the host's `epoch` - one per
 * life of the host window, new after a reload - and a revision that goes up
 * by one per message.
 */
export type SequencerState = {
	/** The host life whose state this window holds; none before a snapshot. */
	epoch?: string
	rev: number
	/** A snapshot was asked of this host life and has not come yet. */
	awaiting?: string
}

export const INITIAL_SEQUENCER: SequencerState = { rev: 0 }

/** What to do with a data message. */
export type Step = 'apply' | 'resync' | 'ignore'

/**
 * Applies data strictly in order. A snapshot is always taken. A patch or
 * actions that follow the last revision of the same host life are applied;
 * anything else - a gap, or a host that reloaded and announces itself only by
 * a new epoch - asks for a snapshot instead, once per host life.
 */
export const sequence = (
	state: SequencerState,
	message: DataBody
): [SequencerState, Step] => {
	if (message.kind === 'snapshot') {
		return [{ epoch: message.epoch, rev: message.rev }, 'apply']
	}
	if (
		state.awaiting === undefined &&
		message.epoch === state.epoch &&
		message.rev === state.rev + 1
	) {
		return [{ ...state, rev: message.rev }, 'apply']
	}
	if (state.awaiting === message.epoch) {
		return [state, 'ignore']
	}
	return [{ ...state, awaiting: message.epoch }, 'resync']
}
